import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, fromEvent } from 'rxjs';

export type SessionWarningState = {
  isActive: boolean;
  /** Wall-clock time when the warning expires and logout should happen */
  endsAt: number;
};

type SessionLeader = {
  tabId: string;
  heartbeatAt: number;
  visible: boolean;
};

@Injectable({
  providedIn: 'root',
})
export class TabActivityService implements OnDestroy {
  private readonly STORAGE_KEY = 'last_activity';
  private readonly TOKEN_REFRESH_KEY = 'last_token_refresh';
  private readonly WARNING_STATE_KEY = 'warning_state';
  private readonly LEADER_KEY = 'session_leader';
  private readonly TOKEN_REFRESH_LOCK = 'lh-token-refresh';

  /**
   * Chrome can throttle hidden-tab timers to ~1/min after ~5 min.
   * Dead threshold must stay well above that or a hidden leader always loses.
   */
  private readonly LEADER_DEAD_MS = 150_000; // 2.5 minutes

  private broadcastChannel: BroadcastChannel;
  private tabId: string;
  private pageHideHandler: (() => void) | null = null;
  private visibilityHandler: (() => void) | null = null;

  private lastActivitySubject = new BehaviorSubject<number>(Date.now());

  // Callbacks for external services
  private logoutCallback?: () => void;
  private continueSessionCallback?: () => void;
  private warningStateCallback?: (isActive: boolean, endsAt: number) => void;

  constructor() {
    this.tabId = crypto.randomUUID();
    this.broadcastChannel = new BroadcastChannel('session_management');
    this.setupBroadcastChannel();
    this.setupStorageListener();
    this.setupPageLifecycle();
    this.updateLastActivity();
  }

  /**
   * Renew leadership if this tab already leads, or claim it if the previous
   * leader's heartbeat is stale / prefer a visible tab. Best-effort only —
   * token refresh uses navigator.locks for a real mutex.
   */
  public claimOrRenewLeadership(): boolean {
    const now = Date.now();
    const visible = document.visibilityState === 'visible';
    const leader = this.readLeader();

    const leaderDead =
      !leader ||
      !leader.tabId ||
      !leader.heartbeatAt ||
      now - leader.heartbeatAt > this.LEADER_DEAD_MS;

    // Hidden tabs should not steal from a still-alive visible leader
    if (
      !visible &&
      leader &&
      !leaderDead &&
      leader.visible &&
      leader.tabId !== this.tabId
    ) {
      return false;
    }

    const canTakeOver =
      leaderDead ||
      leader.tabId === this.tabId ||
      // Prefer the tab the user is actively looking at
      (visible && leader && leader.visible === false);

    if (!canTakeOver) {
      return false;
    }

    const next: SessionLeader = {
      tabId: this.tabId,
      heartbeatAt: now,
      visible,
    };
    localStorage.setItem(this.LEADER_KEY, JSON.stringify(next));

    // Re-read to cut some read-then-write races (still not fully atomic)
    const confirmed = this.readLeader();
    return confirmed?.tabId === this.tabId;
  }

  /**
   * Cross-tab mutex for token refresh. Falls back to running work directly
   * when Web Locks API is unavailable.
   */
  public runWithTokenRefreshLock(work: () => Promise<void>): Promise<void> {
    const locks = navigator.locks;
    if (!locks?.request) {
      return work();
    }
    return locks.request(this.TOKEN_REFRESH_LOCK, () => work());
  }

  /**
   * Active warning from storage (may already be past endsAt — caller decides).
   */
  public getWarningStateIfActive(): SessionWarningState | null {
    const state = this.getWarningState();
    if (!state?.isActive || !(state.endsAt > 0)) {
      return null;
    }
    return state;
  }

  public releaseLeadershipIfHeld(): void {
    const leader = this.readLeader();
    if (leader?.tabId === this.tabId) {
      localStorage.removeItem(this.LEADER_KEY);
    }
  }

  private readLeader(): SessionLeader | null {
    try {
      const raw = localStorage.getItem(this.LEADER_KEY);
      return raw ? (JSON.parse(raw) as SessionLeader) : null;
    } catch {
      return null;
    }
  }

  private setupPageLifecycle() {
    this.pageHideHandler = () => this.releaseLeadershipIfHeld();
    window.addEventListener('pagehide', this.pageHideHandler);

    this.visibilityHandler = () => {
      if (document.visibilityState === 'visible') {
        this.claimOrRenewLeadership();
      } else {
        // Keep heartbeat accurate so others know we are hidden
        const leader = this.readLeader();
        if (leader?.tabId === this.tabId) {
          localStorage.setItem(
            this.LEADER_KEY,
            JSON.stringify({
              tabId: this.tabId,
              heartbeatAt: Date.now(),
              visible: false,
            }),
          );
        }
      }
    };
    document.addEventListener('visibilitychange', this.visibilityHandler);
  }

  private setupBroadcastChannel() {
    this.broadcastChannel.onmessage = (event) => {
      if (event.data.type === 'activity_update') {
        this.handleActivityUpdate(event.data.timestamp);
      } else if (event.data.type === 'token_refresh') {
        this.handleTokenRefresh(event.data.timestamp);
      } else if (event.data.type === 'logout') {
        console.log('Logout message received from another tab');
        this.logoutCallback?.();
      } else if (event.data.type === 'continue_session') {
        console.log('Continue session message received from another tab');
        this.continueSessionCallback?.();
      } else if (event.data.type === 'warning_state') {
        console.log('Warning state message received from another tab');
        this.handleWarningState(event.data.isActive, event.data.endsAt ?? 0);
      }
    };
  }

  private setupStorageListener() {
    fromEvent(window, 'storage').subscribe((event: Event) => {
      const storageEvent = event as StorageEvent;
      if (storageEvent.key === this.STORAGE_KEY) {
        const timestamp = Number(storageEvent.newValue);
        if (!isNaN(timestamp)) {
          this.lastActivitySubject.next(timestamp);
        }
      } else if (storageEvent.key === this.WARNING_STATE_KEY) {
        try {
          const warningState = JSON.parse(
            storageEvent.newValue || '{}',
          ) as SessionWarningState;
          if (
            warningState.isActive !== undefined &&
            warningState.endsAt !== undefined
          ) {
            this.warningStateCallback?.(
              warningState.isActive,
              warningState.endsAt,
            );
          }
        } catch (e) {
          console.error('Error parsing warning state from storage:', e);
        }
      }
    });
  }

  public updateLastActivity() {
    const timestamp = Date.now();
    localStorage.setItem(this.STORAGE_KEY, timestamp.toString());
    this.lastActivitySubject.next(timestamp);
    this.broadcastChannel.postMessage({
      type: 'activity_update',
      timestamp,
      tabId: this.tabId,
    });
  }

  public updateTokenRefreshTime(timestamp: number) {
    localStorage.setItem(this.TOKEN_REFRESH_KEY, timestamp.toString());
    this.broadcastChannel.postMessage({
      type: 'token_refresh',
      timestamp,
      tabId: this.tabId,
    });
  }

  private handleActivityUpdate(timestamp: number) {
    const currentLastActivity = this.lastActivitySubject.value;
    if (timestamp > currentLastActivity) {
      localStorage.setItem(this.STORAGE_KEY, timestamp.toString());
      this.lastActivitySubject.next(timestamp);
    }
  }

  private handleTokenRefresh(timestamp: number) {
    localStorage.setItem(this.TOKEN_REFRESH_KEY, timestamp.toString());
  }

  private handleWarningState(isActive: boolean, endsAt: number) {
    const warningState: SessionWarningState = { isActive, endsAt };
    localStorage.setItem(this.WARNING_STATE_KEY, JSON.stringify(warningState));
    this.warningStateCallback?.(isActive, endsAt);
  }

  public getLastActivity(): number {
    return this.getStoredTimestamp(this.STORAGE_KEY);
  }

  public getLastTokenRefresh(): number {
    return this.getStoredTimestamp(this.TOKEN_REFRESH_KEY);
  }

  private getStoredTimestamp(key: string): number {
    const stored = localStorage.getItem(key);
    return stored ? Number(stored) : Date.now();
  }

  public continueSession(): void {
    this.broadcastChannel.postMessage({ type: 'continue_session' });
    this.updateLastActivity();
  }

  public logout(): void {
    this.broadcastChannel.postMessage({
      type: 'logout',
      timestamp: Date.now(),
      tabId: this.tabId,
    });
  }

  public broadcastWarningState(isActive: boolean, endsAt: number): void {
    const warningState: SessionWarningState = { isActive, endsAt };
    localStorage.setItem(this.WARNING_STATE_KEY, JSON.stringify(warningState));
    this.broadcastChannel.postMessage({
      type: 'warning_state',
      isActive,
      endsAt,
      tabId: this.tabId,
    });
  }

  private getWarningState(): SessionWarningState | null {
    try {
      const stored = localStorage.getItem(this.WARNING_STATE_KEY);
      if (stored) {
        return JSON.parse(stored) as SessionWarningState;
      }
    } catch (e) {
      console.error('Error parsing warning state from storage:', e);
    }
    return null;
  }

  // Set callbacks for external services
  public setCallback(
    type: 'logout' | 'continue_session' | 'warning_state',
    callback: (() => void) | ((isActive: boolean, endsAt: number) => void),
  ): void {
    if (type === 'logout') {
      this.logoutCallback = callback as () => void;
    } else if (type === 'continue_session') {
      this.continueSessionCallback = callback as () => void;
    } else if (type === 'warning_state') {
      this.warningStateCallback = callback as (
        isActive: boolean,
        endsAt: number,
      ) => void;
    }
  }

  ngOnDestroy() {
    if (this.pageHideHandler) {
      window.removeEventListener('pagehide', this.pageHideHandler);
      this.pageHideHandler = null;
    }
    if (this.visibilityHandler) {
      document.removeEventListener('visibilitychange', this.visibilityHandler);
      this.visibilityHandler = null;
    }
    this.releaseLeadershipIfHeld();
    this.broadcastChannel.close();
  }
}
